import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";
import SEO from "../components/SEO";
import { Reveal, MaskedLines, Eyebrow } from "../components/Reveal";
import { RateCards, useRates } from "../components/GoldRate";
import LocationsSection from "../components/LocationsSection";
export const RkIntro = () => (
  <section className="bg-[#F8F4EB] py-24 sm:py-36" data-testid="rk-intro-section">
    <Reveal className="max-w-3xl mx-auto px-6 text-center">
      <img src="/images/rk-logo.png" alt="RK Group" className="h-20 sm:h-24 w-auto mx-auto" data-testid="rk-intro-logo" />
      <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-12 text-[#1E232A]">
        The <span className="italic text-[#C5A059]">RK Group</span>
      </h2>
      <p className="text-base sm:text-lg text-[#1E232A]/70 leading-loose mt-12">
        RK Group is an established Indian family business house built on generations of trust, strong
        relationships, and an enduring commitment to excellence. With a distinguished presence across jewellery
        and real estate, the Group brings together two respected enterprises: Ramesh Kapoor and Sons Jewellers
        Private Limited and RK Builders. From craftsmanship, manufacturing, and trusted wholesale partnerships to
        thoughtfully planned residential developments, every endeavour reflects the Group’s values, experience,
        and long-term vision.
      </p>
      <Link to="/our-story" data-testid="rk-intro-read-more" className="group inline-flex items-center gap-4 mt-14">
        <span className="text-[11px] uppercase tracking-[0.32em] font-semibold text-[#1E232A] group-hover:text-[#C5A059] transition-colors duration-500">
          Read More
        </span>
        <span className="w-11 h-11 rounded-full border border-[#1E232A]/25 flex items-center justify-center transition-all duration-500 group-hover:bg-[#C5A059] group-hover:border-[#C5A059]">
          <ArrowUpRight size={16} className="text-[#1E232A] transition-all duration-500 group-hover:text-[#F8F4EB] group-hover:rotate-45" />
        </span>
      </Link>
    </Reveal>
  </section>
);

export const DirectorsFeature = () => (
  <section className="bg-[#060A12]" data-testid="directors-feature">
    <div className="grid grid-cols-1 lg:grid-cols-12">
      <div className="lg:col-span-7 relative min-h-[60vh] lg:min-h-[92vh] overflow-hidden">
        <img
          src="/images/directors.jpg"
          alt="Mr. Ramesh Chand Kapoor, Mr. Vishal Kapoor and Mr. Krrish Kapoor — Directors, RK Group"
          className="absolute inset-0 w-full h-full object-cover object-top"
          data-testid="directors-feature-image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#060A12]/60 via-transparent to-transparent lg:bg-gradient-to-r lg:from-transparent lg:via-transparent lg:to-[#060A12]" />
      </div>
      <div className="lg:col-span-5 flex items-center px-6 sm:px-12 lg:px-16 xl:px-20 py-20 lg:py-0 rk-grain relative">
        <Reveal className="w-full text-center">
          <p className="text-[11px] uppercase tracking-[0.35em] text-[#C5A059] font-semibold">
            The Directors
          </p>
          <h2 className="font-serif font-light text-4xl sm:text-5xl xl:text-6xl tracking-tight text-[#FBF9F5] leading-[1.08] mt-8">
            Built across generations.{" "}
            <span className="italic text-[#C5A059]">United by one vision.</span>
          </h2>
          <div className="w-16 h-px bg-[#C5A059]/60 mt-10 mx-auto" />
          <Link to="/leadership" data-testid="directors-feature-cta" className="group inline-flex items-center gap-4 mt-12">
            <span className="text-[11px] uppercase tracking-[0.32em] font-semibold text-[#FBF9F5] group-hover:text-[#C5A059] transition-colors duration-500">
              Meet Our Leadership
            </span>
            <span className="w-11 h-11 rounded-full border border-[#FBF9F5]/25 flex items-center justify-center transition-all duration-500 group-hover:bg-[#C5A059] group-hover:border-[#C5A059]">
              <ArrowUpRight size={16} className="text-[#FBF9F5] transition-all duration-500 group-hover:text-[#060A12] group-hover:rotate-45" />
            </span>
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

export const StatementBand = ({
  line = "A Legacy of Trust, Across Generations.",
  sub = "RK Group",
}) => (
  <div className="bg-[#EFECE6] border-y border-[#C5A059]/25 py-12 sm:py-16" data-testid="editorial-statement">
    <Reveal>
      <div className="max-w-4xl mx-auto px-6 text-center">
        <div className="flex items-center justify-center gap-5 mb-6">
          <span className="h-px w-12 sm:w-20 bg-[#C5A059]/50" />
          <span className="w-1.5 h-1.5 rotate-45 bg-[#C5A059]" />
          <span className="h-px w-12 sm:w-20 bg-[#C5A059]/50" />
        </div>
        <p className="font-serif italic text-2xl sm:text-3xl lg:text-4xl text-[#1E232A] leading-snug">
          {line}
        </p>
        <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.35em] text-[#1E232A]/50 mt-6 font-sans font-semibold">
          {sub}
        </p>
      </div>
    </Reveal>
  </div>
);

const CountUp = ({ value }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    const duration = 1800;
    let frame;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      setDisplay(Math.round(value * (1 - Math.pow(1 - p, 3))));
      if (p < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [inView, value]);
  return <span ref={ref}>{display}</span>;
};

const GLANCE_STATS = [
  { value: 50, suffix: "+", label: "Years of Legacy" },
  { value: 2, suffix: "", label: "Established Business Verticals", sub: "Jewellery · Real Estate" },
  { value: 5, suffix: "", label: "Directors" },
  { value: 3, suffix: "", label: "Jewellery Locations", sub: "Meerut · Delhi" },
  { value: 27, suffix: "+", label: "Residential Developments" },
  { text: "Multiple", suffix: "", label: "Real Estate Locations" },
];

export const LEGACY_DEVELOPMENTS = [
  "Palm Green City",
  "Shree Shyam Vatika",
  "Shree Radha Kunj",
  "Golden Enclave",
  "Aluna City",
  "Radha Enclave",
  "Shree Shyam Enclave",
  "Sai Enclave",
];

const FOOTPRINT_POINTS = [
  { name: "Sardhana", x: 170, y: 165 },
  { name: "Lawar", x: 320, y: 100 },
  { name: "Khardoni", x: 505, y: 175 },
  { name: "Ramraj", x: 690, y: 110 },
  { name: "Putthi", x: 850, y: 195 },
  { name: "Mawana", x: 415, y: 290 },
  { name: "Kharkhadi", x: 620, y: 325 },
  { name: "Kharkhauda", x: 800, y: 365 },
  { name: "Modinagar", x: 280, y: 385 },
];

export const FootprintMap = () => {
  const path = FOOTPRINT_POINTS.map((p) => `${p.x},${p.y}`).join(" ");
  return (
    <div className="relative border border-[#C5A059]/15 bg-[#0B1320] px-4 py-10 sm:p-12 overflow-hidden" data-testid="footprint-section">
      <svg viewBox="0 0 1000 480" className="w-full h-auto" role="img" aria-label="RK Builders development footprint map" data-testid="footprint-map">
        <polyline points={path} fill="none" stroke="#C5A059" strokeOpacity="0.22" strokeWidth="1" />
        <line x1="170" y1="165" x2="415" y2="290" stroke="#C5A059" strokeOpacity="0.15" strokeWidth="1" strokeDasharray="3 6" />
        <line x1="690" y1="110" x2="620" y2="325" stroke="#C5A059" strokeOpacity="0.15" strokeWidth="1" strokeDasharray="3 6" />
        <line x1="850" y1="195" x2="800" y2="365" stroke="#C5A059" strokeOpacity="0.15" strokeWidth="1" strokeDasharray="3 6" />
        <line x1="320" y1="100" x2="280" y2="385" stroke="#C5A059" strokeOpacity="0.15" strokeWidth="1" strokeDasharray="3 6" />
        {FOOTPRINT_POINTS.map((p, i) => (
          <g key={p.name}>
            <circle cx={p.x} cy={p.y} r="16" fill="#C5A059" className="rk-pulse" style={{ animationDelay: `${i * 0.45}s` }} />
            <circle cx={p.x} cy={p.y} r="3.5" fill="#C5A059" />
            <text x={p.x} y={p.y - 18} textAnchor="middle" fill="#94A3B8" fontSize="14" letterSpacing="2.5"
              style={{ fontFamily: "Manrope, sans-serif", fontWeight: 600 }}>
              {p.name.toUpperCase()}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
};

const WHAT_WE_CREATE = [
  { num: "01", title: "Planned Societies", body: "Thoughtfully structured residential communities." },
  { num: "02", title: "Developed Land", body: "Planned development built around infrastructure and future value." },
  { num: "03", title: "Residential Plots", body: "Spaces where families can build their vision of home." },
  { num: "04", title: "Villas & Communities", body: "Residential environments created around comfort, belonging and quality of life." },
];

const Chapter = ({ num, eyebrow, title, light = false }) => (
  <div className="flex items-start gap-6 mb-10 sm:mb-14">
    <span className={`font-serif text-5xl sm:text-7xl leading-none ${light ? "text-[#1E232A]/15" : "text-[#C5A059]/25"}`}>
      {num}
    </span>
    <div>
      <Eyebrow light={light}>{eyebrow}</Eyebrow>
      <h2 className={`font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-3 ${light ? "text-[#1E232A]" : "text-[#FBF9F5]"}`}>
        {title}
      </h2>
    </div>
  </div>
);

export default function Home() {
  const [content, setContent] = useState(null);
  const [directors, setDirectors] = useState([]);
  const { rates, loading, refresh } = useRates();

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const textY = useTransform(scrollYProgress, [0, 1], [0, -110]);
  const textOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  useEffect(() => {
    Promise.all([
      api.get("/content").then((r) => r.data).catch(() => null),
      api.get("/directors").then((r) => r.data).catch(() => []),
    ]).then(([c, d]) => {
      setContent(c);
      setDirectors(d);
    });
  }, []);

  const timeline = content?.timeline || [];

  return (
    <div data-testid="home-page">
      <SEO description="RK Group brings Ramesh Kapoor and Sons Jewellers Pvt. Ltd. and RK Builders under one name — wholesale and retail jewellery, residential developments, townships, developed land, and villas." />

      {/* SECTION 1 — OPENING SCREEN */}
      <section ref={heroRef} className="relative min-h-screen flex flex-col overflow-hidden rk-grain" data-testid="hero-section">
        <div className="absolute inset-0 bg-[#060A12]">
          <video
            data-testid="hero-video"
            className="rk-hero-video w-full h-full object-cover"
            poster="/videos/elephants-poster.jpg"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            disablePictureInPicture
            aria-hidden="true"
          >
            <source src="/videos/elephants.webm" type="video/webm" />
            <source src="/videos/elephants.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-[#060A12]/45" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#060A12]/75 via-[#060A12]/20 to-[#060A12]" />
        </div>

        <motion.div
          style={{ y: textY, opacity: textOpacity }}
          className="relative flex-1 flex items-center justify-center px-5 sm:px-8 pt-28 pb-16"
        >
          <div className="text-center max-w-4xl">
            <motion.p
              initial={{ opacity: 0, letterSpacing: "0.6em" }}
              animate={{ opacity: 1, letterSpacing: "0.35em" }}
              transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
              className="text-[11px] sm:text-xs uppercase text-[#C5A059] font-semibold mb-7 sm:mb-8"
            >
              RK Group
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.15 }}
              className="font-serif italic text-lg sm:text-2xl text-[#FBF9F5]/75 mb-6 sm:mb-7"
            >
              Strength. Legacy. Continuity.
            </motion.p>
            <MaskedLines
              lines={["Jewellery. Real Estate.", "One Family-Led Group."]}
              className="rk-hero-h font-serif font-light tracking-tight text-[#FBF9F5] leading-[1.08] [text-shadow:0_2px_40px_rgba(6,10,18,0.6)]"
            />
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.9 }}
              className="text-sm sm:text-base text-[#FBF9F5]/70 leading-loose max-w-xl mx-auto mt-8 sm:mt-10 px-2 sm:px-0"
            >
              RK Group brings Ramesh Kapoor and Sons Jewellers Pvt. Ltd. and RK Builders under one name.
              Our work spans wholesale and retail jewellery, residential developments, townships, developed
              land, and villas.
            </motion.p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="relative pb-8 flex justify-center"
        >
          <div className="w-px h-14 bg-gradient-to-b from-transparent via-[#C5A059]/60 to-transparent" />
        </motion.div>
      </section>

      <RkIntro />

      <DirectorsFeature />

      {/* SECTION 2 — OUR STORY */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24 sm:py-32" data-testid="story-section">
        <Reveal><Chapter num="01" eyebrow="Our Story" title="Built Through Work. Continued by Family." /></Reveal>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5" delay={0.1}>
            <p className="text-base text-[#94A3B8] leading-loose">
              RK Group represents the businesses built and led by the Kapoor family. Under the leadership of
              Ramesh Chand Kapoor, the group operates across jewellery and real estate. Today, the family works
              together to strengthen established operations and develop the next phase of the group.
            </p>
            <Link to="/our-story" data-testid="story-read-more-link"
              className="inline-flex items-center gap-2 mt-8 text-[11px] uppercase tracking-[0.22em] font-semibold text-[#C5A059] hover:gap-4 transition-all duration-300">
              Read the full story <ArrowRight size={14} />
            </Link>
          </Reveal>
          <div className="lg:col-span-7 relative pl-8 border-l border-[#C5A059]/20">
            {(timeline.length ? timeline : [{ title: "Timeline loading…", date: null }]).slice(0, 6).map((t, i) => (
              <Reveal key={i} delay={i * 0.06} className="mb-8 relative">
                <span className="absolute -left-[37px] top-1.5 w-2 h-2 rounded-full bg-[#C5A059]" />
                <p className="font-serif text-xl sm:text-2xl text-[#FBF9F5]">{t.title}</p>
                <p className="text-xs text-[#94A3B8] mt-1 uppercase tracking-[0.2em]">
                  {t.date || "Date to be verified"}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3 — GROUP AT A GLANCE */}
      <section className="bg-[#0B1320] border-y border-[#C5A059]/10 py-24 sm:py-32" data-testid="glance-section">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <Reveal><Chapter num="02" eyebrow="The Group" title="RK Group at a Glance" /></Reveal>
          <Reveal delay={0.05}>
            <p className="font-serif italic text-lg sm:text-xl text-[#94A3B8] -mt-6 mb-12 sm:mb-16">
              Five decades of enterprise. Built across generations. Growing across industries.
            </p>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/5">
            {GLANCE_STATS.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.06} className="bg-[#0B1320] p-6 sm:p-10 lg:p-14 relative min-w-0">
                <span className="absolute top-0 left-6 sm:left-10 lg:left-14 w-10 h-px bg-[#C5A059]/50" />
                <p className="rk-stat font-serif font-light text-[#FBF9F5] break-words" data-testid={`glance-stat-${i}`}>
                  {s.text ? s.text : <CountUp value={s.value} />}
                  {s.suffix && <span className="text-[#C5A059]">{s.suffix}</span>}
                </p>
                <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.2em] text-[#C5A059] mt-4 sm:mt-5 font-semibold leading-relaxed">{s.label}</p>
                {s.sub && (
                  <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.16em] text-[#94A3B8] mt-2 leading-relaxed">{s.sub}</p>
                )}
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4 — BUSINESS VERTICALS */}
      <section className="py-24 sm:py-32" data-testid="verticals-section">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <Reveal><Chapter num="03" eyebrow="Two Businesses" title="One Group. Two Verticals." /></Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Reveal className="group overflow-hidden border border-white/10 min-h-[520px] flex flex-col">
              <div className="flex-1 min-h-[320px] bg-black flex items-center justify-center p-8 sm:p-12">
                <img src="/images/rks-logo.jpg" alt="Ramesh Kapoor and Sons Jewellers — RKS emblem"
                  className="max-h-[280px] sm:max-h-[340px] w-auto object-contain transition-transform duration-[1.4s] ease-out group-hover:scale-[1.03]" />
              </div>
              <div className="bg-[#0B1320] border-t border-[#C5A059]/15 p-8 sm:p-12">
                <Eyebrow>The Jewellery House</Eyebrow>
                <h3 className="font-serif text-3xl sm:text-4xl text-[#FBF9F5] mt-3 leading-tight">
                  Ramesh Kapoor and Sons Jewellers Pvt. Ltd.
                </h3>
                <p className="text-sm text-[#94A3B8] leading-relaxed mt-5 max-w-lg">
                  Wholesale is the main jewellery business. Our flagship wholesale office operates from Sarafa
                  Bazar, Meerut, with a second wholesale office in Karol Bagh, Delhi. Our Meerut retail showroom
                  serves families buying for weddings, festivals, gifting, anniversaries, and personal occasions.
                </p>
                <Link to="/jewellery" data-testid="vertical-enter-jewellery-btn"
                  className="inline-block mt-8 text-[11px] uppercase tracking-[0.22em] font-semibold bg-[#C5A059] text-[#060A12] px-8 py-4 hover:bg-[#d6b678] transition-colors duration-300">
                  Enter Jewellery
                </Link>
              </div>
            </Reveal>
            <Reveal delay={0.12} className="group overflow-hidden border border-white/10 min-h-[520px] flex flex-col">
              <div className="flex-1 min-h-[320px] bg-black flex items-center justify-center p-8 sm:p-12">
                <img src="/images/rk-builders-logo.jpg" alt="RK Builders — Building a Better Tomorrow emblem"
                  className="max-h-[280px] sm:max-h-[340px] w-auto object-contain transition-transform duration-[1.4s] ease-out group-hover:scale-[1.03]" />
              </div>
              <div className="bg-[#0B1320] border-t border-[#C5A059]/15 p-8 sm:p-12">
                <Eyebrow>The Building Arm</Eyebrow>
                <h3 className="font-serif text-3xl sm:text-4xl text-[#FBF9F5] mt-3 leading-tight">RK Builders</h3>
                <p className="text-sm text-[#94A3B8] leading-relaxed mt-5 max-w-lg">
                  RK Builders undertakes residential projects, townships, developed land, and villa developments
                  across Sardhana, Lawar, Ramraj, Putthi, Khardoni, Mawana, Kharkhadi, Kharkhauda,
                  Modinagar, and other upcoming locations.
                </p>
                <Link to="/projects" data-testid="vertical-explore-projects-btn"
                  className="inline-block mt-8 text-[11px] uppercase tracking-[0.22em] font-semibold border border-[#C5A059]/60 text-[#C5A059] px-8 py-4 hover:bg-[#C5A059] hover:text-[#060A12] transition-colors duration-300">
                  Explore Projects
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SECTION 5 — LIVE GOLD RATE */}
      <section className="bg-[#0B1320] border-y border-[#C5A059]/10 py-24 sm:py-28" data-testid="home-rates-section">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <Reveal><Chapter num="04" eyebrow="Live Bullion Desk" title="Today’s Gold & Silver Rate" /></Reveal>
          <Reveal delay={0.1}>
            <RateCards rates={rates} loading={loading} refresh={refresh} />
          </Reveal>
        </div>
      </section>

      {/* SECTION 6 — RK BUILDERS LEGACY */}
      <section className="py-24 sm:py-32" data-testid="builders-section">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <Reveal>
            <Eyebrow>RK Builders</Eyebrow>
            <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#FBF9F5] mt-4 max-w-3xl leading-[1.08]">
              Building Communities. <span className="italic text-[#C5A059]">Creating Legacies.</span>
            </h2>
            <p className="text-base text-[#94A3B8] leading-loose mt-8 max-w-2xl">
              Over the years, RK Builders has shaped thoughtfully planned residential developments with a focus
              on organised infrastructure, enduring relationships and long-term value. Every development
              represents another chapter in our journey of creating places people are proud to call home.
            </p>
          </Reveal>

          {/* Achievement counters */}
          {(() => {
            const s = content?.builders_stats || { developments: 27, signature: 8, locations_word: "Multiple", legacy_word: "One" };
            const stats = [
              { value: s.developments, suffix: "+", label: "Developments" },
              { word: s.locations_word, label: "Locations" },
              { value: s.signature, suffix: "", label: "Signature Developments" },
              { word: s.legacy_word, label: "Enduring Legacy" },
            ];
            return (
              <div className="grid grid-cols-2 lg:grid-cols-4 mt-20 sm:mt-28 border-t border-[#C5A059]/15" data-testid="builders-stats">
                {stats.map((st, i) => (
                  <Reveal key={st.label} delay={i * 0.08}
                    className={`pt-10 sm:pt-14 pb-8 sm:pb-10 pr-4 sm:pr-6 min-w-0 ${i > 0 ? "border-l border-[#C5A059]/15 pl-5 sm:pl-10" : ""} ${i === 2 ? "max-lg:border-l-0 max-lg:pl-0" : ""}`}>
                    <p className="rk-stat-lg font-serif font-light text-[#FBF9F5] break-words" data-testid={`builders-stat-${i}`}>
                      {st.word ? <span className="italic text-[#C5A059]">{st.word}</span> : <CountUp value={st.value} />}
                      {st.suffix && <span className="text-[#C5A059]">{st.suffix}</span>}
                    </p>
                    <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.2em] sm:tracking-[0.3em] text-[#C5A059] mt-4 sm:mt-5 font-semibold leading-relaxed">{st.label}</p>
                  </Reveal>
                ))}
              </div>
            );
          })()}

          {/* Development legacy wall */}
          <div className="mt-24 sm:mt-32">
            <Reveal>
              <Eyebrow>Our Development Legacy</Eyebrow>
              <h3 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl text-[#FBF9F5] mt-4">
                Developments That Define Our Journey
              </h3>
            </Reveal>
            <div className="mt-14 flex flex-wrap items-center" data-testid="legacy-wall">
              {LEGACY_DEVELOPMENTS.map((name, i) => (
                <div key={name} className="flex items-center">
                  <Reveal delay={i * 0.05} className="group relative py-4 sm:py-6">
                    <span className="block font-serif font-light text-2xl sm:text-4xl lg:text-[44px] text-[#FBF9F5]/75 group-hover:text-[#C5A059] transition-colors duration-500 cursor-default leading-tight"
                      data-testid={`legacy-item-${i}`}>
                      {name}
                    </span>
                    <span className="block h-5 text-[10px] uppercase tracking-[0.25em] text-[#94A3B8] opacity-0 group-hover:opacity-100 transition-opacity duration-500 mt-1">
                      An RK Builders Development
                    </span>
                  </Reveal>
                  {i < LEGACY_DEVELOPMENTS.length - 1 && (
                    <span className="mx-5 sm:mx-9 w-px h-8 sm:h-12 bg-[#C5A059]/25 shrink-0 self-center" aria-hidden="true" />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Footprint */}
          <div className="mt-24 sm:mt-32">
            <Reveal>
              <Eyebrow>Our Footprint</Eyebrow>
              <h3 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl text-[#FBF9F5] mt-4">
                A Growing Development Footprint
              </h3>
            </Reveal>
            <Reveal delay={0.1} className="mt-12">
              <FootprintMap />
              <p className="font-serif italic text-lg sm:text-xl text-[#94A3B8] text-center mt-10">
                Rooted in the region. Growing with every new community.
              </p>
            </Reveal>
          </div>

          {/* What we create */}
          <div className="mt-24 sm:mt-32 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-14" data-testid="what-we-create">
            {WHAT_WE_CREATE.map((w, i) => (
              <Reveal key={w.num} delay={i * 0.07} className="border-t border-[#C5A059]/25 pt-8">
                <span className="font-serif font-light text-5xl text-[#C5A059]/45">{w.num}</span>
                <h4 className="font-serif text-2xl text-[#FBF9F5] mt-5">{w.title}</h4>
                <p className="text-sm text-[#94A3B8] leading-relaxed mt-3">{w.body}</p>
              </Reveal>
            ))}
          </div>

          {/* Final impact statement */}
          <Reveal className="mt-28 sm:mt-40 text-center max-w-4xl mx-auto">
            <p className="font-serif font-light text-3xl sm:text-5xl lg:text-6xl text-[#FBF9F5] leading-[1.15] tracking-tight">
              We don’t simply develop land.
            </p>
            <p className="font-serif italic font-light text-3xl sm:text-5xl lg:text-6xl text-[#C5A059] leading-[1.15] tracking-tight mt-3">
              We create the foundations for communities to grow.
            </p>
            <p className="text-sm sm:text-base text-[#94A3B8] leading-loose mt-10 max-w-2xl mx-auto">
              With every development, RK Builders continues to build on a legacy of thoughtful planning,
              enduring relationships and long-term value creation.
            </p>
            <Link to="/real-estate" data-testid="builders-discover-cta"
              className="group inline-flex items-center gap-4 mt-12">
              <span className="text-[11px] uppercase tracking-[0.32em] font-semibold text-[#FBF9F5] group-hover:text-[#C5A059] transition-colors duration-500">
                Discover RK Builders
              </span>
              <span className="w-11 h-11 rounded-full border border-[#C5A059]/50 flex items-center justify-center transition-all duration-500 group-hover:bg-[#C5A059]">
                <ArrowRight size={15} className="text-[#C5A059] transition-all duration-500 group-hover:text-[#060A12] group-hover:translate-x-0.5" />
              </span>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* SECTION 7 — JEWELLERY BUSINESS */}
      <section className="bg-[#FBF9F5] text-[#1E232A] py-24 sm:py-32" data-testid="jewellery-business-section">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <Chapter num="06" eyebrow="The Hero Business" title="Wholesale Is the Core of Our Jewellery Business." light />
            <p className="text-base text-[#1E232A]/70 leading-loose">
              Our flagship wholesale office operates from Sarafa Bazar, Meerut. We also have a wholesale office in
              Karol Bagh, Delhi. Our stock covers gold jewellery, diamond jewellery, bridal sets, chains, bangles,
              rings, bracelets, earrings, mangalsutras, pendants, gold and silver coins, silver jewellery, and
              silver products.
            </p>
            <p className="text-base text-[#1E232A]/70 leading-loose mt-5">
              Our Meerut retail showroom serves individual customers and families. Customers can select jewellery
              according to their budget, occasion, preferred design, and weight.
            </p>
            <div className="flex flex-wrap gap-3 mt-10">
              <Link to="/jewellery/wholesale" data-testid="jb-explore-wholesale-btn"
                className="text-[11px] uppercase tracking-[0.2em] font-semibold bg-[#1E232A] text-[#FBF9F5] px-6 py-3.5 hover:bg-[#C5A059] hover:text-[#060A12] transition-colors duration-300">
                Explore Wholesale
              </Link>
              <Link to="/jewellery/retail" data-testid="jb-explore-retail-btn"
                className="text-[11px] uppercase tracking-[0.2em] font-semibold border border-[#1E232A]/30 text-[#1E232A] px-6 py-3.5 hover:border-[#C5A059] hover:text-[#C5A059] transition-colors duration-300">
                Explore Retail
              </Link>
              <Link to="/jewellery/catalogue" data-testid="jb-request-catalogue-btn"
                className="text-[11px] uppercase tracking-[0.2em] font-semibold border border-[#1E232A]/30 text-[#1E232A] px-6 py-3.5 hover:border-[#C5A059] hover:text-[#C5A059] transition-colors duration-300">
                Request Catalogue
              </Link>
              <Link to="/jewellery/rates" data-testid="jb-gold-rate-btn"
                className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#C5A059] px-6 py-3.5 hover:text-[#1E232A] transition-colors duration-300">
                View Today’s Gold Rate
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="relative">
            <div className="aspect-[4/5] overflow-hidden border border-[#1E232A]/10">
              <img src="/images/jewellery-bridal.jpg" alt="Bridal jewellery collection" className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* SECTION 8 — LEADERSHIP */}
      <section className="py-24 sm:py-32" data-testid="leadership-section">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <Reveal><Chapter num="07" eyebrow="The Kapoor Family" title="Leadership Across Generations" /></Reveal>
          <div className="flex flex-wrap justify-center gap-6" data-testid="leadership-grid">
            {directors.map((d, i) => (
              <Reveal key={d.id} delay={i * 0.07}
                className="group w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] xl:w-[calc(20%-19.2px)]">
                <div className="h-full border border-white/10 p-6 sm:p-8 text-center hover:border-[#C5A059]/40 transition-colors duration-500 flex flex-col items-center">
                  <span className="text-[10px] tracking-[0.3em] text-[#C5A059]/50 font-semibold">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {d.image ? (
                    <img src={d.image} alt={d.name}
                      className="w-20 h-20 mt-4 rounded-full object-cover object-top border border-[#C5A059]/40 shrink-0" />
                  ) : (
                    <div className="w-20 h-20 mt-4 rounded-full border border-[#C5A059]/40 flex items-center justify-center bg-[#0B1320] shrink-0">
                      <span className="font-serif text-2xl text-[#C5A059]">
                        {d.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                      </span>
                    </div>
                  )}
                  <h3 className="font-serif text-xl text-[#FBF9F5] mt-5 min-h-[3.5rem] flex items-center justify-center leading-snug">
                    {d.name}
                  </h3>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] mt-2 min-h-[2.5rem] leading-relaxed">
                    {d.role}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-10 text-center">
            <Link to="/leadership" data-testid="leadership-view-all-btn"
              className="inline-block text-[11px] uppercase tracking-[0.22em] font-semibold text-[#C5A059] border-b border-[#C5A059]/40 pb-1 hover:border-[#C5A059] transition-colors duration-300">
              Meet the leadership
            </Link>
          </Reveal>
        </div>
      </section>

      {/* SECTION 9 — LOCATIONS */}
      <LocationsSection />

      {/* SECTION 10 — SPEAK TO THE RIGHT TEAM */}
      <section className="py-24 sm:py-32 rk-grain relative" data-testid="contact-routes-section">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <Reveal><Chapter num="09" eyebrow="Contact" title="Speak to the Right Team" /></Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/5">
            {[
              { label: "Wholesale Jewellery", to: "/jewellery/wholesale#enquire", sub: "For jewellers & retailers" },
              { label: "Retail Jewellery", to: "/jewellery/appointment", sub: "Book a showroom appointment" },
              { label: "Property Enquiry", to: "/contact?type=property", sub: "Plots, villas & townships" },
              { label: "Book a Site Visit", to: "/site-visit", sub: "Visit any project location" },
              { label: "Landowner Proposal", to: "/landowner-partnerships", sub: "Joint development & land sale" },
              { label: "Business Partnership", to: "/contact?type=partnership", sub: "Vendors, partners & institutions" },
            ].map((c, i) => (
              <Link key={c.label} to={c.to} data-testid={`contact-route-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="group bg-[#060A12] p-8 sm:p-10 hover:bg-[#0B1320] transition-colors duration-500">
                <span className="font-serif text-3xl text-[#C5A059]/30 group-hover:text-[#C5A059]/60 transition-colors duration-500">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="font-serif text-2xl text-[#FBF9F5] mt-4 group-hover:text-[#C5A059] transition-colors duration-500">
                  {c.label}
                </h3>
                <p className="text-xs text-[#94A3B8] mt-2 uppercase tracking-[0.15em]">{c.sub}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
